import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { resetHours, today, useDateShape } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import type { RegistrSVFormData } from './context.types';

export const getDefaultValues = (): RegistrSVFormData => ({
	typOsoby: 'FO',
	ico: undefined,
	filterText: null,
	datumDo: null,
	datumOd: null,
	filterVehicleStatus: {
		actual: true,
		historical: false,
	},
	filterVztah: {
		vlastnik: false,
		provozovatel: false,
		vse: false,
	},
	filterStatus: {
		provozovane: false,
		zanik: false,
		vse: false,
	},
	pageSize: 20,
	page: 1,
});

interface FormDefinitionInstanceProps {
	code: PageCode;
	defaultValues: RegistrSVFormData;
}

export function FormDefinitionInstance({
	code,
	defaultValues,
}: FormDefinitionInstanceProps): FormDefinition<RegistrSVFormData> {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const { getDatePickerShape } = useDateShape();
	const tsn = getLocalizeCurried(code);

	const formSchema = yup
		.object({
			typOsoby: yup.string().oneOf(['FO', 'PFO']).required(),
			ico: yup.string(),
			filterText: yup.string().nullable(),
			datumOd: getDatePickerShape({
				isInvalidMessage: tsn('formular.validace.datum-od.min'),
			})
				.nullable()
				.min(
					resetHours(today('1900-01-01')),
					tsn('formular.validace.datum-od.min')
				)
				.max(today(), tsn('formular.validace.datum-od.max')),
			datumDo: getDatePickerShape({
				isInvalidMessage: tsn('formular.validace.datum-do.min'),
			})
				.nullable()
				.min(
					resetHours(today('1900-01-01')),
					tsn('formular.validace.datum-do.min')
				)
				.max(today(), tsn('formular.validace.datum-do.max'))
				.min(
					yup.ref('datumOd'),
					tsn('formular.validace.datum-do.starsi-nez-datum-od')
				),
			filterVehicleStatus: yup.object({
				actual: yup.boolean().required(),
				historical: yup.boolean().required(),
			}),
			filterVztah: yup.object({
				vlastnik: yup.boolean().required(),
				provozovatel: yup.boolean().required(),
				vse: yup.boolean().required(),
			}),
			filterStatus: yup.object({
				provozovane: yup.boolean().required(),
				zanik: yup.boolean().required(),
				vse: yup.boolean().required(),
			}),
			page: yup.number().required(),
			pageSize: yup.number().required(),
		})
		.required();

	const formMethods = useGovForm<RegistrSVFormData>({
		formSchema,
		defaultValues,
	});

	return getFormDefinition({ formMethods, formSchema });
}
